import { trpc } from "@/lib/trpc";
import type { QboSyncPendingChange } from "@payrollmart-invoice/shared";
import { Link2, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

/**
 * The changes the nightly QuickBooks sync refused to make on its own.
 *
 * Sits above the full mapping table so the short list of things actually
 * waiting on a decision is the first thing on the page — the table below is
 * the whole customer book and buries them.
 */
export function PendingQboChanges() {
	const utils = trpc.useUtils();
	const { data, isLoading } = trpc.qbo.pendingCustomerChanges.useQuery();
	const mapMutation = trpc.qbo.mapCustomerToQbo.useMutation();
	const [linking, setLinking] = useState<string | null>(null);

	async function handleLink(change: QboSyncPendingChange) {
		if (!change.suggestedCustomerId) return;
		setLinking(change.qboCustomerId);
		try {
			await mapMutation.mutateAsync({
				customerId: change.suggestedCustomerId,
				qboCustomerId: change.qboCustomerId,
			});
			await Promise.all([
				utils.customers.list.invalidate(),
				utils.qbo.pendingCustomerChanges.invalidate(),
				utils.qbo.bannerStatus.invalidate(),
			]);
			toast.success(`Linked ${change.suggestedName} to ${change.qboName}`);
		} catch (error) {
			toast.error(error instanceof Error ? error.message : "Failed to link customer");
		} finally {
			setLinking(null);
		}
	}

	if (isLoading || !data || data.pending.length === 0) return null;

	return (
		<div className="mb-6 rounded-lg border border-[#bfdbfe] bg-[#eff6ff]">
			<div className="border-b border-[#bfdbfe] px-6 py-4">
				<h2 className="flex items-center gap-2 text-sm font-semibold text-[#1e40af]">
					<TriangleAlert className="h-4 w-4" strokeWidth={2} />
					{data.pending.length} QuickBooks{" "}
					{data.pending.length === 1 ? "change needs" : "changes need"} a decision
				</h2>
				<p className="mt-1 text-xs text-[#1e40af]/80">
					From the nightly sync
					{data.ranAt ? ` on ${new Date(data.ranAt).toLocaleDateString()}` : ""}. Contact details
					and exact name matches were applied automatically; these were not.
				</p>
			</div>

			<ul className="divide-y divide-[#bfdbfe]">
				{data.pending.map((change) => (
					<li
						key={change.qboCustomerId}
						className="flex items-center gap-4 px-6 py-3 text-sm text-[#1e3a5f]"
					>
						<div className="min-w-0 flex-1">
							<p className="truncate font-medium">{change.qboName}</p>
							<p className="text-xs text-[#1e40af]/80">
								{change.kind === "near_match"
									? `Looks like "${change.suggestedName}" — names differ, so billing was not repointed automatically.`
									: "New in QuickBooks with no matching customer here."}
							</p>
						</div>

						{change.kind === "near_match" && change.suggestedCustomerId ? (
							<button
								type="button"
								onClick={() => handleLink(change)}
								disabled={linking === change.qboCustomerId}
								className="inline-flex shrink-0 items-center gap-1.5 rounded border border-[#4f46e5] px-2.5 py-1 text-xs font-medium text-[#4f46e5] hover:bg-[#eef2ff] disabled:opacity-50"
							>
								<Link2 className="h-3.5 w-3.5" strokeWidth={2} />
								{linking === change.qboCustomerId ? "Linking..." : "Link these"}
							</button>
						) : (
							<span className="shrink-0 text-xs text-[#1e40af]/70">Map it in the table below</span>
						)}
					</li>
				))}
			</ul>
		</div>
	);
}
